import { lazy, Suspense, useState } from 'react';
import { isDebugBuild, isTuningEligible } from '@playcastdotio/PeerCore';
import { getHostStreamTuningControl } from '../../coordinators/streamTuning/initializeStreamTuning';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';
import { BeaconDisplay } from './components/BeaconDisplay';
import { BeaconController } from './BeaconController';
import { StreamDebugOverlayHost } from './components/StreamDebugOverlayHost';
import { displayOutputManager } from '../../coordinators/init';
import { useDisplayOutput } from '../../coordinators/media/hooks/useDisplayOutput';
import type { DisplayOutputManager } from '../../coordinators/media/managers/DisplayOutputManager';
const StreamTuningPanelHost = lazy(() => import('./components/StreamTuningPanelHost').then(module => ({ default: module.StreamTuningPanelHost })));

function BeaconSidebarPreview({ manager }: { manager: DisplayOutputManager }) {
  const displayOutput = useDisplayOutput(manager);
  return (
    <BeaconDisplay
      displays={displayOutput.displays}
      selectedDisplayId={displayOutput.selectedDisplayId}
      isLive={displayOutput.isLive}
      elapsedTime={displayOutput.elapsedTime}
      isDisplaySelected={displayOutput.displays.length > 0}
      onDisplayClick={displayOutput.onDisplayClick}
    />
  );
}

const BeaconSidebar = () => {
  const [showPreview, setShowPreview] = useState(false);

  return (
    <div className='relative hidden flex-col gap-4 lg:flex lg:h-full'>
      {showPreview && displayOutputManager ? (
        <BeaconSidebarPreview manager={displayOutputManager} />
      ) : (
        <div className='theme-midnight:bg-slate-800 bg-darker border-primary flex min-h-[80px] items-center justify-center rounded-lg border-2'>
          <span className='text-sm text-white/40'>Preview hidden</span>
        </div>
      )}
      <button
        type='button'
        onClick={() => setShowPreview((prev) => !prev)}
        className='absolute right-2 top-2 z-10 flex items-center gap-x-1.5 rounded bg-black/50 px-2 py-1 text-xs text-white/70 hover:bg-black/70 hover:text-white'
      >
        <FontAwesomeIcon icon={showPreview ? faEyeSlash : faEye} className='h-3 w-3' />
        {showPreview ? 'Hide' : 'Show'}
      </button>
    </div>
  );
};

export const BeaconPlayerControls = () => {
  return (
    <div className='flex w-full flex-col gap-4 overflow-y-auto p-4 lg:flex-row lg:overflow-hidden'>
      <div className='order-2 flex h-[309px] w-full flex-none flex-col gap-4 lg:order-1 lg:h-full lg:w-96'>
        {/* Stream debug overlay: always on Beacon host UI (developer machine). */}
        <StreamDebugOverlayHost />
        {isTuningEligible(isDebugBuild() || process.env.NODE_ENV === 'development', window.location.search) &&
          <Suspense fallback={null}><StreamTuningPanelHost getControl={getHostStreamTuningControl} /></Suspense>}
        <BeaconSidebar />
      </div>
      <div className='order-1 flex w-full flex-col gap-4 rounded-lg lg:order-2'>
        <BeaconController />
      </div>
    </div>
  );
};
